import { immerable } from 'immer';

var _a, _b;
class PromptInput {
    constructor() {
        this[_a] = true;
    }
    static fromJSON(params = {}) {
        const instance = new this();
        const defaults = {
            minContribution: 0,
            weight: 1,
        };
        const result = Object.assign(instance, defaults, params);
        if (!result.type || !result.version || !result.id || !result.config) {
            throw new Error("Incomplete PromptInput configuration");
        }
        return result;
    }
    /**
     * Creates an InputAssessment if input can be auto assessed, otherwise
     * undefined and assessment has to be done manually
     * @param input
     */
    autoAssess(input) {
        return undefined;
    }
    isFilledIn(input) {
        return false;
    }
    getSubmission(submissions) {
        return submissions[this.id];
    }
    /**
     * Override if the input can provide less than 0
     */
    getMinMax() {
        return { max: this.weight, min: 0 };
    }
    /**
     * Returns a string representation of the submission, in the form of
     * "<startTag type [points] of [maxPoints]>answer</endTag>"
     *
     * @param submissions
     * @returns
     */
    getInputAnswer(submission, assessment) {
        return undefined;
    }
    toJSON() {
        return {
            config: this.config,
            id: this.id,
            minContribution: this.minContribution,
            type: this.type,
            version: this.version,
            weight: this.weight,
        };
    }
}
_a = immerable;
class Prompt {
    constructor() {
        this[_b] = true;
    }
    static fromJSON(params) {
        var _c;
        const instance = new Prompt();
        const defaults = {
            isTeam: false,
            category: "prompt",
            name: "Prompt",
            description: "<p>Describe the question here</p>",
            correctPoints: 10,
            submitPoints: 0,
            duration: 1,
            requireAnyInputs: false,
            useAIAssessment: false,
        };
        const result = Object.assign(instance, defaults, params, {
            inputs: ((_c = params.inputs) === null || _c === void 0 ? void 0 : _c.map(promptInputFactory)) || [],
        });
        if (!result.id) {
            throw new Error("Incomplete Prompt configuration");
        }
        return result;
    }
    autoAssess(inputSubmissions) {
        const inputAssessments = {};
        let atLeastOneAssessed = false;
        for (const promptInput of this.inputs) {
            const inputSubmission = inputSubmissions[promptInput.id];
            if (!inputSubmission && !this.requireAnyInputs) {
                throw new Error(`Missing input submission for input ${promptInput.id} ${promptInput.type}`);
            }
            if (inputSubmission) {
                atLeastOneAssessed = true;
                const assessment = promptInput.autoAssess(inputSubmission);
                if (assessment) {
                    inputAssessments[promptInput.id] = assessment;
                }
            }
        }
        if (!atLeastOneAssessed) {
            throw new Error(`Missing input submissions`);
        }
        return inputAssessments;
    }
    isFilledIn(inputSubmissions) {
        if (!inputSubmissions) {
            return false;
        }
        let atLeastOneFilledIn = false;
        for (const promptInput of this.inputs) {
            const inputSubmission = inputSubmissions[promptInput.id];
            if (!inputSubmission) {
                if (!this.requireAnyInputs) {
                    return false;
                }
                else {
                    continue;
                }
            }
            if (!inputSubmission || !promptInput.isFilledIn(inputSubmission)) {
                if (!this.requireAnyInputs) {
                    return false;
                }
                else {
                    continue;
                }
            }
            atLeastOneFilledIn = true;
        }
        return atLeastOneFilledIn;
    }
    getFileInputs(inputSubmissions) {
        if (!inputSubmissions) {
            return [];
        }
        const files = [];
        for (const promptInput of this.inputs) {
            const inputSubmission = inputSubmissions[promptInput.id];
            if (!inputSubmission && !this.requireAnyInputs) {
                throw new Error(`Missing input submission for input ${promptInput.id} ${promptInput.type}`);
            }
            if (inputSubmission && promptInput.type === "upload") {
                const upload = inputSubmission;
                const file = upload.file;
                const url = upload.url;
                if (file) {
                    files.push(inputSubmission);
                }
                else {
                    if (!url && !this.requireAnyInputs) {
                        throw new Error(`Missing input submission for input ${promptInput.id} ${promptInput.type}`);
                    }
                }
            }
        }
        return files;
    }
    getMinMax() {
        const totalWeight = this.inputs.reduce((t, v) => t + Math.max(1, v.weight), 0);
        const minMaxes = this.inputs.map((i) => i.getMinMax());
        return minMaxes.map((m) => {
            var _c;
            return ({
                min: m.min / totalWeight,
                max: m.max / totalWeight,
                options: (_c = m.options) === null || _c === void 0 ? void 0 : _c.map((v) => v / totalWeight),
            });
        });
    }
    get maxPoints() {
        return this.submitPoints + this.correctPoints;
    }
    getAssessmentValues(assessments) {
        //       score = maxScore* sum( pi[i].weight* a[i].grading ) / sum(pi.weight)
        const inputs = this.inputs;
        const totalWeight = inputs.reduce((t, v) => t + v.weight, 0);
        const assessedWeight = inputs.reduce((t, i) => { var _c; return t + (((_c = assessments[i.id]) === null || _c === void 0 ? void 0 : _c.value) || 0) * i.weight; }, 0);
        const ratio = Math.max(0, assessedWeight / totalWeight);
        const correctPoints = this.correctPoints * ratio;
        const points = correctPoints + this.submitPoints;
        return { points, correctPoints };
    }
    toJSON() {
        return {
            id: this.id,
            category: this.category,
            isTeam: this.isTeam,
            name: this.name,
            description: this.description,
            inputs: this.inputs.map((i) => typeof i.toJSON === "function" ? i.toJSON() : i),
            correctPoints: this.correctPoints,
            submitPoints: this.submitPoints,
            duration: this.duration,
            requireAnyInputs: this.requireAnyInputs || false,
            useAIAssessment: this.useAIAssessment || false,
        };
    }
}
_b = immerable;
const promptInputFactory = (input) => {
    const ctor = promptInputRegistry[input.type];
    if (!ctor) {
        throw new Error("Unknown prompt input type: " + input.type);
    }
    return ctor.fromJSON(input);
};

class OptionPromptInput extends PromptInput {
    constructor() {
        super();
    }
    autoAssess(submission) {
        const { optionWeights, multi } = this.config;
        if (!optionWeights) {
            return undefined;
        }
        const result = { id: this.id, isAuto: true, value: 0 };
        let totalWeight = multi
            ? optionWeights.reduce((t, v) => t + Math.max(v, 0), 0)
            : Math.max(...optionWeights);
        let value = 0;
        submission.values.forEach(str => {
            const ix = Number.parseInt(str);
            value += optionWeights[ix];
        });
        result.value = Math.max(this.minContribution || 0, value / totalWeight || 0);
        return result;
    }
    getInputAnswer(submission, assessment) {
        const { optionWeights, multi } = this.config;
        return {
            id: this.id,
            type: `${multi ? 'multi' : 'single'}-option`,
            points: assessment === null || assessment === void 0 ? void 0 : assessment.value,
            maxPoints: this.weight,
            answer: submission.values.map(v => this.config.options[Number.parseInt(v)])
        };
    }
    isFilledIn(submission) {
        var _a;
        return ((_a = submission.values) === null || _a === void 0 ? void 0 : _a.length) > 0;
    }
    getMinMax() {
        const { optionWeights, multi } = this.config;
        const positiveMax = optionWeights.reduce((t, v) => t + Math.max(v), 0);
        const negativeMax = multi ? optionWeights.reduce((t, v) => t + Math.min(v), 0) : Math.min(...optionWeights);
        return {
            min: Math.max(this.minContribution, negativeMax / positiveMax),
            max: this.weight,
            options: optionWeights.map(v => v / positiveMax)
        };
    }
}

class TextPromptInput extends PromptInput {
    constructor() {
        super();
    }
    isFilledIn(submission) {
        var _a;
        return ((_a = submission.value) === null || _a === void 0 ? void 0 : _a.trim().length) > 0;
    }
    autoAssess(submission) {
        const { answers, autoCorrectable, answerWeights, matchCase } = this.config;
        if (!autoCorrectable || !(answers === null || answers === void 0 ? void 0 : answers.length) || !(answerWeights === null || answerWeights === void 0 ? void 0 : answerWeights.length)) {
            return undefined;
        }
        const result = { id: this.id, isAuto: true, value: 0 };
        let totalWeight = Math.max(...answerWeights);
        const rawAnswer = (submission.value || '').trim();
        if (rawAnswer) {
            const answer = matchCase ? rawAnswer : rawAnswer.toLowerCase();
            const ix = answers.findIndex(value => {
                if (answer == (matchCase ? value : value.toLowerCase())) {
                    return true;
                }
            });
            if (ix >= 0) {
                result.value = Math.max(this.minContribution || 0, answerWeights[ix] / totalWeight || 0);
            }
        }
        return result;
    }
    getInputAnswer(submission, assessment) {
        return {
            id: this.id,
            type: 'text',
            points: assessment === null || assessment === void 0 ? void 0 : assessment.value,
            maxPoints: this.weight,
            answer: [submission.value]
        };
    }
}

class UploadPromptInput extends PromptInput {
    constructor() {
        super();
    }
    isFilledIn(submission) {
        return submission.file != undefined || submission.fileObjectId != undefined;
    }
}

const promptInputRegistry = {
    'option': OptionPromptInput,
    'text': TextPromptInput,
    'upload': UploadPromptInput,
};

const isAnInstructor = (subject) => (subject === null || subject === void 0 ? void 0 : subject.roles.some(r => r.endsWith('Instructor'))) || false;
const isAStudent = (subject) => (subject === null || subject === void 0 ? void 0 : subject.roles.some(r => r.endsWith('Learner'))) || false;
const isAnAdmin = (subject) => (subject === null || subject === void 0 ? void 0 : subject.roles.some(r => r.endsWith('Administrator'))) || false;

export { OptionPromptInput, Prompt, PromptInput, TextPromptInput, UploadPromptInput, isAStudent, isAnAdmin, isAnInstructor, promptInputFactory, promptInputRegistry };
//# sourceMappingURL=index.mjs.map
